import { memo } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Checkbox, TooltipProvider } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import { RoleChip } from '../RoleChip';
import { SearchInput } from '../SearchInput';
import { UserAvatar } from '../UserAvatar';
import { HighlightText } from '../data-table';
import { RoleFilterBar, type RoleFilterOption } from './RoleFilterBar';
import type { RbacPerson } from './types';

export interface UserListPanelProps {
  rows: readonly RbacPerson[];
  templateNameById: ReadonlyMap<string, string>;
  selectedUserId: string | null;
  canManage: boolean;
  search: string;
  /** Role filter pills (All + one per occurring role) and the active selection. */
  roleOptions: RoleFilterOption[];
  roleFilter: string;
  /** Bulk-assign mode — rows grow a checkbox and clicks toggle selection. */
  bulkMode: boolean;
  bulkSelectedIds: ReadonlySet<string>;
  isLoading?: boolean;
  /** Omit to hide the per-row reset affordance (apps without a default role). */
  onResetUser?: (row: RbacPerson) => void;
  onSearchChange: (value: string) => void;
  onRoleFilterChange: (value: string) => void;
  onSelectUser: (userId: string) => void;
  onToggleBulkUser: (userId: string) => void;
  searchPlaceholder?: string;
}

/**
 * Left pane of the User-permissions split — the searchable directory of people.
 *
 * The pane carries the divider between the two sides (`border-r`), with equal
 * breathing room either side of the line. The border spans the full content
 * height because flex items stretch, so it reads as one continuous rule — and the
 * top gap is this pane's own `pt-4` (padding sits INSIDE the border box) rather
 * than the page wrapper's, so the divider starts flush against the toolbar's
 * bottom border instead of 16px below it.
 */
export const UserListPanel = memo(function UserListPanel({
  rows,
  templateNameById,
  selectedUserId,
  canManage,
  search,
  roleOptions,
  roleFilter,
  bulkMode,
  bulkSelectedIds,
  isLoading = false,
  onResetUser,
  onSearchChange,
  onRoleFilterChange,
  onSelectUser,
  onToggleBulkUser,
  searchPlaceholder = 'Search by name, email or role…',
}: UserListPanelProps) {
  return (
    // Provider is OURS — each row's RoleChip renders a tooltip; see TemplateMatrix.
    <TooltipProvider delayDuration={200}>
      <aside className="border-border flex min-h-0 w-full flex-none flex-col border-r pr-5 pt-4 lg:w-[22rem]">
        <SearchInput
          value={search}
          onChange={onSearchChange}
          debounce={0}
          placeholder={searchPlaceholder}
          // The shared SearchInput ships h-6 (`size="sm"`) or the default h-10 — h-6
          // reads too small here, so keep the default size and pin the field to h-8
          // (the app's standard control height) via inputClassName. The light fill is
          // the same grey as the module bands, so both read as page chrome.
          inputClassName="h-8 bg-gray-50"
          className="w-full"
        />

        <RoleFilterBar options={roleOptions} value={roleFilter} onChange={onRoleFilterChange} />

        <div className="-mx-2 min-h-0 flex-1 overflow-y-auto pb-2">
          {isLoading ? (
            <div className="flex justify-center py-10 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            </div>
          ) : rows.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm leading-[1.15] text-gray-500">
              No users found.
            </p>
          ) : (
            rows.map((row) => {
              const isSel = !bulkMode && selectedUserId === row.userId;
              const templateName =
                row.templateId != null ? templateNameById.get(row.templateId) : undefined;
              // The reset affordance only applies to explicit assignments, but its
              // SLOT is reserved on every row (invisible when inapplicable) — a
              // conditionally-rendered slot pushed those rows' role chips left,
              // leaving the chip column ragged.
              const showResetSlot = canManage && !bulkMode && onResetUser != null;
              const canReset = showResetSlot && row.hasAssignment;
              return (
                <button
                  key={row.userId}
                  type="button"
                  onClick={() =>
                    bulkMode ? onToggleBulkUser(row.userId) : onSelectUser(row.userId)
                  }
                  className={cn(
                    'group flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-left transition-colors',
                    isSel ? 'bg-primary/5' : 'hover:bg-white/60',
                  )}
                >
                  {bulkMode && (
                    <Checkbox
                      checked={bulkSelectedIds.has(row.userId)}
                      onCheckedChange={() => onToggleBulkUser(row.userId)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${row.name}`}
                    />
                  )}

                  <UserAvatar name={row.name} className="h-8 w-8 shrink-0 text-sm" />

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-lg font-medium text-gray-700">
                        <HighlightText text={row.name} query={search} />
                      </span>
                      {row.overrideCount > 0 ? (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          aria-label={`${row.overrideCount} override${row.overrideCount === 1 ? '' : 's'}`}
                        />
                      ) : null}
                    </span>
                    <span className="block truncate text-sm text-gray-500">
                      <HighlightText text={row.email ?? '—'} query={search} />
                    </span>
                  </span>

                  <RoleChip roleName={templateName} className="shrink-0" />

                  {showResetSlot && (
                    <span
                      role={canReset ? 'button' : undefined}
                      tabIndex={canReset ? 0 : -1}
                      aria-label={canReset ? `Reset ${row.name} to the default role` : undefined}
                      aria-hidden={!canReset}
                      title={canReset ? 'Reset to default role' : undefined}
                      onClick={
                        canReset
                          ? (e) => {
                              e.stopPropagation();
                              onResetUser?.(row);
                            }
                          : undefined
                      }
                      onKeyDown={
                        canReset
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                onResetUser?.(row);
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'flex shrink-0 rounded p-0.5 text-gray-500 transition-opacity',
                        canReset
                          ? 'opacity-0 hover:text-red-400 focus-visible:opacity-100 group-hover:opacity-100'
                          : 'invisible',
                      )}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
});
